import { MultiplexPanelChannelInput, MultiplexPanelRevision, MultiplexPanelView } from 'interfaces/multiplexPanels';
import { StainType } from 'interfaces/stainType';
import { filter, find, findKey, first, flatMap, includes, map, max, sortBy, toLower, uniq } from 'lodash';

export interface PanelChannelDraft extends MultiplexPanelChannelInput {
  needsModalityChoice?: boolean;
  rnaProbeId?: string;
}

export interface NewPanelDraft {
  panel: MultiplexPanelView;
  channels: PanelChannelDraft[];
}

export const initializePanelChannels = (
  panel: MultiplexPanelView,
  stains: StainType[],
  sourceSlideId?: string,
  displayNames: Record<string, string> = {}
): PanelChannelDraft[] =>
  map(sortBy(panel.channels, 'channelIndex'), (channel) => {
    const sourceSlide = find(panel.slides, { slideId: sourceSlideId });
    const slideChannelId =
      sourceSlide && findKey(sourceSlide.channelMapping, (index) => index === channel.channelIndex);
    const currentMarker = slideChannelId !== undefined ? sourceSlide?.channelMarkerTypes[slideChannelId] : undefined;
    const currentIsRna = slideChannelId !== undefined && sourceSlide?.rnaChannelIds.includes(slideChannelId);
    const currentModalities = uniq(
      map(panel.slides, (slide) => {
        const channelId = findKey(slide.channelMapping, (index) => index === channel.channelIndex);
        return channelId !== undefined && slide.rnaChannelIds.includes(channelId) ? 'rna' : 'non-rna';
      })
    );
    const stainTypeId =
      sourceSlide && !currentIsRna
        ? channel.saved && currentMarker === channel.saved.analysisName
          ? channel.saved.stainTypeId
          : currentMarker
        : channel.saved?.stainTypeId || first(channel.suggestions)?.stainTypeId;
    const stain = find(stains, { id: stainTypeId });
    const channelModality = sourceSlide
      ? currentIsRna
        ? 'rna'
        : stain?.dna
        ? 'dna'
        : 'mif'
      : channel.saved?.channelModality || (first(currentModalities) === 'rna' ? 'rna' : stain?.dna ? 'dna' : 'mif');
    const analysisName = channel.saved?.analysisName || `${first(channel.identifiers.sourceNames)}-rna`;
    return {
      channelIndex: channel.channelIndex,
      identifiers: channel.identifiers,
      channelModality,
      stainTypeId: stain?.id || null,
      displayName:
        channelModality === 'rna'
          ? channel.saved?.displayName || displayNames[analysisName] || stain?.displayName || ''
          : null,
      repeatConfirmed: channel.saved?.repeatConfirmed || false,
      needsModalityChoice: !sourceSlide && !channel.saved && currentModalities.length > 1,
      rnaProbeId: channel.saved?.channelModality === 'rna' ? channel.saved.analysisName : undefined,
    };
  });

export const validatePanelChannels = (
  channels: PanelChannelDraft[],
  stains: StainType[],
  panelId: string,
  revisions: MultiplexPanelRevision[] = []
) => {
  const issues: Record<number, string[]> = {};
  const analysisNames: Record<number, string> = {};
  const usedNames = new Map<string, { index: number; modality: string }>();
  const repeatCounts = new Map<string, number>();
  const catalogIds = new Set(map(stains, (stain) => toLower(stain.id)));
  const reservedProbeIds = new Set([
    ...map(
      filter(channels, (channel) => channel.channelModality === 'rna' && Boolean(channel.rnaProbeId)),
      'rnaProbeId'
    ),
    ...map(filter(flatMap(filter(revisions, { panelId }), 'channels'), { channelModality: 'rna' }), 'analysisName'),
  ]);
  let probeNumber = 1;
  const addIssue = (index: number, message: string) => {
    issues[index] = [...(issues[index] || []), message];
  };

  for (const channel of sortBy(channels, 'channelIndex')) {
    const index = channel.channelIndex;
    const stain = find(stains, { id: channel.stainTypeId });
    if (channel.needsModalityChoice) addIssue(index, 'Mixed current settings. Choose the type to apply.');
    let analysisName: string;
    if (channel.channelModality === 'rna') {
      if (!channel.displayName?.trim()) addIssue(index, 'Enter an RNA probe display name.');
      if (channel.rnaProbeId) analysisName = channel.rnaProbeId;
      else {
        analysisName = `${panelId}-rna-prob${probeNumber}`;
        while (reservedProbeIds.has(analysisName)) {
          probeNumber += 1;
          analysisName = `${panelId}-rna-prob${probeNumber}`;
        }
        reservedProbeIds.add(analysisName);
        probeNumber += 1;
      }
      if (catalogIds.has(toLower(analysisName))) addIssue(index, 'RNA analysis name conflicts with a stain ID.');
    } else {
      if (!stain || stain.deletedAt) {
        addIssue(index, 'Choose an available stain. For a missing stain, email support@nucleaimd.com.');
        continue;
      }
      if ((channel.channelModality === 'dna') !== Boolean(stain.dna))
        addIssue(index, 'Type must match the selected stain.');
      const repeatKey = `${stain.id}:${channel.channelModality}`;
      const instance = (repeatCounts.get(repeatKey) || 0) + 1;
      repeatCounts.set(repeatKey, instance);
      analysisName = stain.id;
      if (instance > 1 && channel.channelModality !== 'dna') {
        if (!channel.repeatConfirmed) addIssue(index, 'Confirm this repeated stain.');
        let suffix = instance;
        while (catalogIds.has(toLower(`${stain.id}-${suffix}`)) || usedNames.has(toLower(`${stain.id}-${suffix}`)))
          suffix += 1;
        analysisName = `${stain.id}-${suffix}`;
      }
    }
    analysisNames[index] = analysisName;
    const normalizedName = toLower(analysisName);
    const previous = usedNames.get(normalizedName);
    if (previous && !(previous.modality === 'dna' && channel.channelModality === 'dna')) {
      addIssue(index, 'Analysis name duplicates another channel.');
      addIssue(previous.index, 'Analysis name duplicates another channel.');
    }
    usedNames.set(normalizedName, { index, modality: channel.channelModality });
  }
  return { issues, analysisNames, invalidChannelCount: Object.keys(issues).length };
};

export const buildPanelSaveRequest = (
  panel: MultiplexPanelView,
  channels: PanelChannelDraft[],
  slideIds: string[]
) => ({
  panel: {
    ...(panel.saved ? { panelId: panel.panelId } : {}),
    channels: map(
      channels,
      ({ needsModalityChoice: _needsModalityChoice, rnaProbeId: _rnaProbeId, ...channel }) => channel
    ),
  },
  applyToSlideIds: slideIds,
});

export const createNewPanelDraft = (
  source: MultiplexPanelView,
  channels: PanelChannelDraft[],
  slideIds: string[],
  existingPanelIds: string[]
): NewPanelDraft => {
  const panelNumber = (max(map(existingPanelIds, (panelId) => Number(panelId.replace('panel-', '')) || 0)) || 0) + 1;
  const copiedChannels = map(channels, ({ rnaProbeId: _rnaProbeId, ...channel }) => ({
    ...channel,
    identifiers: { sourceNames: [...channel.identifiers.sourceNames] },
  }));
  return {
    panel: {
      panelId: `panel-${panelNumber}`,
      saved: false,
      channels: map(copiedChannels, (channel) => ({
        channelIndex: channel.channelIndex,
        identifiers: channel.identifiers,
        suggestions: [],
      })),
      slides: filter([...source.slides, ...(source.candidateSlides || [])], (slide) =>
        includes(slideIds, slide.slideId)
      ),
    },
    channels: copiedChannels,
  };
};

export const areSelectedSlidesApprovable = (panel: MultiplexPanelView, slideIds: string[]) =>
  panel.saved &&
  slideIds.length > 0 &&
  slideIds.every((slideId) =>
    Boolean(find(panel.slides, (slide) => slide.slideId === slideId && slide.approvalStatus !== 'diverged'))
  );
